import { cn } from "@do/ui/lib/utils";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";

type FooterInputVoiceVisualizerProps = {
  barWidth?: number;
  gap?: number;
  sampleIntervalMs?: number;
  speakThreshold?: number;
  minBarHeight?: number;
  maxBarHeight?: number;
  sensitivity?: number;
};

export function FooterInputVoiceVisualizer({
  barWidth = 2,
  gap = 2,
  sampleIntervalMs = 100,
  minBarHeight = 4,
  maxBarHeight = 32,
  sensitivity = 2,
}: FooterInputVoiceVisualizerProps) {
  const t = useTranslations("do-chat-v2");

  const containerRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);

  const [dims, setDims] = useState({ width: 0, height: 0 });
  const [levels, setLevels] = useState<number[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const rafRef = useRef<number | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const dataRef = useRef<Uint8Array<ArrayBuffer> | null>(null);
  const lastTickRef = useRef<number>(0);
  const lastSampleRef = useRef<number>(0);
  const offsetRef = useRef<number>(0);

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0].contentRect;
      setDims({ width: r.width, height: r.height });
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const bars = Math.max(1, Math.floor(dims.width / (barWidth + gap)));
  const pxPerSample = barWidth + gap;
  const pxPerMs = sampleIntervalMs > 0 ? pxPerSample / sampleIntervalMs : pxPerSample / 80;

  useEffect(() => {
    let mounted = true;

    async function start() {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const AC = window.AudioContext || (window as any).webkitAudioContext;
      const ctx = new AC();
      audioCtxRef.current = ctx;
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      setIsRecording(true);

      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      ctx.createMediaStreamSource(stream).connect(analyser);
      analyserRef.current = analyser;
      dataRef.current = new Uint8Array(analyser.fftSize);

      function loop(ts: number) {
        rafRef.current = requestAnimationFrame(loop);

        const lastTs = lastTickRef.current || ts;
        const delta = ts - lastTs;
        lastTickRef.current = ts;

        offsetRef.current = Math.min(pxPerSample, offsetRef.current + delta * pxPerMs);
        if (trackRef.current) {
          trackRef.current.style.transform = `translateX(-${offsetRef.current}px)`;
        }

        if (ts - lastSampleRef.current >= sampleIntervalMs) {
          lastSampleRef.current = ts;
          offsetRef.current = 0;

          if (analyserRef.current && dataRef.current) {
            analyserRef.current.getByteTimeDomainData(dataRef.current);
            const arr = dataRef.current;
            let sum = 0;
            for (let i = 0; i < arr.length; i++) sum += Math.abs(arr[i] - 128);
            const avg = arr.length > 0 ? sum / arr.length : 0;
            const normalized = avg / 64;
            const level = Math.min(1, normalized * sensitivity);

            setLevels((prev) => {
              const next = prev.length >= bars ? [...prev.slice(1), level] : [...prev, level];
              return next;
            });
          }
        }
      }

      rafRef.current = requestAnimationFrame(loop);
    }

    function stop() {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      lastTickRef.current = 0;
      lastSampleRef.current = 0;
      offsetRef.current = 0;

      if (trackRef.current) trackRef.current.style.transform = "translateX(0)";

      try {
        void audioCtxRef.current?.close();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }

      audioCtxRef.current = null;

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      analyserRef.current = null;
      dataRef.current = null;
      setLevels([]);
      setIsRecording(false);
    }

    if (bars > 0 && dims.width > 0 && dims.height > 0 && mounted) {
      void start();
    } else {
      stop();
    }

    return () => {
      mounted = false;
      stop();
    };
  }, [bars, dims.width, dims.height, pxPerMs, pxPerSample, sampleIntervalMs, sensitivity]);

  const statusText = isRecording
    ? t("chat.footer.accessibility.voice-visualizer-status-recording")
    : t("chat.footer.accessibility.voice-visualizer-status-stopped");

  return (
    <div
      id="footer-input-voice-visualizer"
      className="w-full"
      role="group"
      aria-label={t("chat.footer.accessibility.voice-visualizer-aria-label")}
    >
      <div
        id="footer-input-voice-visualizer-container"
        className={cn(
          "w-full",
          "h-(--footer-input-min-height)",
          "rounded-(--footer-input-border-radius)",
          "border-(length:--footer-input-border-width)",
          "border-(--footer-input-border-color)/(--footer-input-border-opacity)",
          "bg-(--footer-input-background-color)/(--footer-input-background-opacity)",
          "px-(--footer-input-padding-x) pr-(--footer-input-padding-right)",
          "py-(--footer-input-padding-y)"
        )}
      >
        <span className="sr-only" aria-live="polite" aria-atomic="true">
          {statusText}
        </span>
        <div id="footer-input-voice-visualizer-container-inner" className="relative w-full h-full">
          <div
            id="footer-input-voice-visualizer-container-inner-blur-left"
            className={cn(
              "pointer-events-none absolute z-1 top-0 left-0 h-full w-[15px]",
              "bg-gradient-to-r from-(--footer-input-background-color)/(--footer-input-background-opacity) to-transparent"
            )}
          />
          <div
            id="footer-input-voice-visualizer-container-inner-blur-right"
            className={cn(
              "pointer-events-none absolute z-1 top-0 right-0 h-full w-[20px]",
              "bg-gradient-to-l from-(--footer-input-background-color)/(--footer-input-background-opacity) to-transparent"
            )}
          />

          <div
            id="footer-input-voice-visualizer-container-inner-track"
            ref={containerRef}
            className="relative w-full h-full overflow-hidden"
            aria-hidden="true"
          >
            <div
              id="footer-input-voice-visualizer-container-inner-track-inner"
              ref={trackRef}
              className="absolute inset-0 flex items-center gap-0.5 will-change-transform"
              style={{ gap: `${gap}px` }}
            >
              {levels.length < bars &&
                Array.from({ length: bars - levels.length }).map((_, i) => (
                  <div
                    id={`footer-input-voice-visualizer-container-inner-track-inner-bar-${i}`}
                    key={`p-${i}`}
                    className={cn(
                      "rounded-full shrink-0",
                      "bg-(--footer-voice-visualizer-inactive-bar-color)/(--footer-voice-visualizer-inactive-bar-color-opacity)"
                    )}
                    style={{ width: `${barWidth}px`, height: `${minBarHeight}px` }}
                  />
                ))}
              {levels.map((v, i) => {
                const h = Math.min(
                  maxBarHeight,
                  Math.max(minBarHeight, Math.round(v * dims.height) * 1.5)
                );
                return (
                  <div
                    id={`footer-input-voice-visualizer-container-inner-track-inner-bar-${i}`}
                    key={i}
                    className={cn(
                      "rounded-full shrink-0",
                      "bg-(--footer-voice-visualizer-active-bar-color)/(--footer-voice-visualizer-active-bar-color-opacity)"
                    )}
                    style={{
                      width: `${barWidth}px`,
                      height: `${h}px`,
                    }}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
